import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChildren,
  QueryList,
  AfterViewInit,
  ChangeDetectorRef,
} from "@angular/core";
import { BfxPopupNotificationService } from "app/bfx-notifications/services/bfx-popup-notification.service";
import { BfxPopupNotificationData } from "app/bfx-notifications/models/bfx-popup-notification-data.model";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
import { BfxPopupNotificationComponent } from "./bfx-popup-notification.component";
import { Subscription } from "rxjs";
import { runDetached } from "../../shared/helper-services/run-detached";
import { NativeDesktopWindow } from "shared/native-desktop/NativeDesktopWindow";
import { WindowType } from "shared/native-desktop/NativeDesktopWindowOptions";
import { ResizeObserverSchedulerTarget } from "shared/helper-services/resize-observer-scheduler-target";
import { GoldenLayoutService } from "app/golden-layout/golden-layout-service";
import { Rect } from "shared/native-desktop/NativeDesktopData";

enum Anchor {
  BottomRight,
  TopRight,
  TopLeft,
  BottomLeft,
}

const FRAME_ID_NOTIFICATIONS: string = "notifications";
const SNAP_DISTANCE: number = 15;

@Component({
  selector: "bfx-popup-notification-container",
  templateUrl: "./html/bfx-popup-notification-container.html",
  standalone: false,
})
export class BfxPopupNotificationContainerComponent implements OnInit, OnDestroy, AfterViewInit {
  public popupNotifications: Array<BfxPopupNotificationData>;
  public popupNotificationsSubscription: Subscription;

  @ViewChildren(BfxPopupNotificationComponent)
  public notifications: QueryList<BfxPopupNotificationComponent>;

  private _window: NativeDesktopWindow;
  private resizeObserver: ResizeObserver;
  private containerElement: Element;
  private notificationConfig: { dockingArea: any; anchor?: Anchor };
  private configSubscription: Subscription;
  private snapDown: boolean = false;

  constructor(
    private popupNotificationService: BfxPopupNotificationService,
    public element: ElementRef,
    public cd: ChangeDetectorRef,
    private goldenLayoutService: GoldenLayoutService,
  ) {}

  ngOnInit() {
    this.popupNotifications = this.popupNotificationService.getPopupNotifications();

    this.popupNotificationsSubscription = this.popupNotificationService.pops$
      .pipe(runDetached(this))
      .subscribe(() => {
        this.popupNotifications = this.popupNotificationService.getPopupNotifications();

        if (NativeDesktopSupport.isNativeDesktopContainer()) {
          if (this.notificationConfig == null) {
            this.onConfigurationLoaded(null, false);
          }

          this.checkResize();
        }
      });
  }

  ngOnDestroy() {
    if (this.popupNotificationsSubscription != null) {
      this.popupNotificationsSubscription.unsubscribe();
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this._window) {
      this._window.close();
    }

    if (this.configSubscription != null && !this.configSubscription.closed) {
      this.configSubscription.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    if (this.isNativeDesktop()) {
      this.initNativeDesktop();
    }
  }

  protected initNativeDesktop(): void {
    this.containerElement = this.element.nativeElement.querySelector(
      ".popup-notification__container",
    );

    // Display configuration changed
    NativeDesktopSupport.desktop.on("display-changed", () => {
      this.checkSnapDown();
      this.checkResize();
    });

    // Notification window was dragged
    NativeDesktopSupport.desktop.on("bounds-changed", (frameId, event) => {
      if (frameId === FRAME_ID_NOTIFICATIONS) {
        let dockingArea = {
          top: event.top,
          left: event.left,
          width: event.right - event.left,
          height: event.bottom - event.top,
        };

        if (this.notificationConfig != null) {
          this.notificationConfig = { dockingArea: dockingArea };
        } else {
          this.notificationConfig.dockingArea = dockingArea;
        }

        this.checkSnapDown();
        this.checkResize();
      }
    });

    NativeDesktopSupport.openWindow(
      FRAME_ID_NOTIFICATIONS,
      WindowType.Notification,
      false,
      null,
      (wnd) => {
        this._window = wnd;
        this._window.getNativeWindow().document.body.appendChild(this.element.nativeElement);

        this._window.show().then(() => {
          this.checkResize();

          if (this.notificationConfig != null) {
            this.onConfigurationLoaded(this.notificationConfig);
          } else {
            this.configSubscription = this.goldenLayoutService.notificationDataSubject.subscribe(
              this.onConfigurationLoaded.bind(this),
            );
          }

          this.resizeObserver = new ResizeObserverSchedulerTarget(
            () => this.checkResize(),
            () => this.containerElement,
            this.constructor.name,
          );
        });
      },
    );
  }

  protected checkResize(): void {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      if (this.notificationConfig != null) {
        this.resizeArea();
      } else if (this._window != null) {
        logger.out.debug(`${this.constructor.name}: Hidding notification area, no config.`);
        this._window.hide();
      }
    }
  }

  protected resizeArea(): void {
    let rect = this.containerElement.getBoundingClientRect();

    logger.out.info(
      `${this.constructor.name}: Adjusting notification area for ` +
        `${this.popupNotifications.length} notifications.`,
    );

    if (this._window != null) {
      if (this.popupNotifications.length == 0) {
        logger.out.debug(`${this.constructor.name}: Hidding notification area.`);
        this._window.hide();
      } else {
        let area = this.notificationConfig.dockingArea;
        if (area == null) {
          this.notificationConfig.dockingArea = area = {};
        }

        let minHeight = this.popupNotifications.length * 105 * NativeDesktopSupport.zoomLevel;
        let minWidth = 450 * NativeDesktopSupport.zoomLevel;

        area.width = Math.max(rect.width, minWidth);
        area.height = Math.max(rect.height, minHeight);

        try {
          logger.out.debug(`${this.constructor.name}: Notification area: ${JSON.stringify(area)}`);
        } catch (e) {
          logger.out.debug(`${this.constructor.name}: Showing notification area.`);
        }

        this.checkArea(area);

        this.goldenLayoutService.setNotificationConfig(this.notificationConfig, false);
        this._window.setBounds(area.left, area.top, area.width, area.height).then(() => {
          this._window.show(true).then(() => {
            // this is deliberately after visibility established
            // as it helps ensure closable notifications in mixed DPI contexts.
            this._window.setMinimumSize(area.width, area.height);
          });
        });
      }
    } else {
      logger.out.warn(`${this.constructor.name}: Notification resize called before window ready.`);
    }
  }

  public isNativeDesktop(): boolean {
    return NativeDesktopSupport.isNativeDesktopContainer();
  }

  protected onConfigurationLoaded(config: any, unsubscribe: boolean = true): void {
    if (this.configSubscription != null && !this.configSubscription.closed && unsubscribe) {
      this.configSubscription.unsubscribe();
    }

    this.notificationConfig = config;
    this.checkSnapDown();

    this.determinePosition();
    this.checkResize();
  }

  protected determinePosition(): void {
    let dockingArea = this.notificationConfig?.dockingArea;
    if (dockingArea?.left == null || dockingArea?.top == null) {
      let anchor = this.notificationConfig?.anchor;

      if (this.notificationConfig == null) {
        this.notificationConfig = { dockingArea: null };
      }

      if (anchor == null) {
        this.notificationConfig.anchor = anchor = Anchor.BottomRight;
      }

      this.notificationConfig.dockingArea = this.getPosition(anchor);
    }
  }

  protected getPosition(anchor: Anchor): { top: number; left: number } {
    let monitorInfo = NativeDesktopSupport.getMonitorInfo();

    switch (anchor) {
      case Anchor.TopLeft:
        return monitorInfo.primaryMonitor.availableRect;
      case Anchor.TopRight:
        return {
          top: monitorInfo.primaryMonitor.availableRect.top,
          left: monitorInfo.primaryMonitor.availableRect.right,
        };
      case Anchor.BottomLeft:
        return {
          top: monitorInfo.primaryMonitor.availableRect.bottom,
          left: monitorInfo.primaryMonitor.availableRect.left,
        };
      case Anchor.BottomRight:
        return {
          top: monitorInfo.primaryMonitor.availableRect.bottom,
          left: monitorInfo.primaryMonitor.availableRect.right,
        };
    }
  }

  protected checkArea(rect: { left: number; top: number; width: number; height: number }): void {
    let monitorInfo = NativeDesktopSupport.getMonitorInfo();

    let area = monitorInfo.virtualScreen;

    rect.left = Math.max(rect.left, area.left);
    rect.top = Math.max(rect.top, area.top);

    if (rect.left + rect.width > area.right) {
      rect.left = area.right - rect.width;
    }

    if (rect.top + rect.height > area.bottom) {
      rect.top = area.bottom - rect.height;
    }

    if (this.snapDown) {
      rect.top = area.bottom - rect.height;
    }
  }

  protected checkSnapDown(): boolean {
    let dockingArea = this.notificationConfig?.dockingArea;

    if (dockingArea?.top != null && dockingArea?.height != null) {
      let monitorInfo = NativeDesktopSupport.getMonitorInfo();

      let area = monitorInfo.virtualScreen;
      let bottom = dockingArea.top + dockingArea.height;

      this.snapDown = bottom + SNAP_DISTANCE > area.bottom;
    }

    return this.snapDown;
  }
}
